import { useEffect, useMemo, useState } from 'react'
import { Fish } from 'lucide-react'
import DatePicker from '../../components/DatePicker'
import { FormDrawer } from '../../components/Drawer'
import { FormBody, FormCols, FormSection, FormSpan, SwitchField } from '../../components/Form'
import FormRow from '../../components/FormRow'
import GroupPicker from '../../components/GroupPicker'
import Select from '../../components/Select'
import TextField from '../../components/TextField'
import { campaignsStrings as S } from './strings'

const EMPTY = {
  name: '',
  templateId: '',
  url: '',
  senderId: '',
  startDate: '',
  endDate: '',
  sendEmailsBy: '',
  groupIds: [],
  programId: '',
  isActive: true,
}

/**
 * إضافة جديدة / تعديل — the phishing campaign form.
 *
 * A campaign is four decisions: what it sends (template), where it points
 * (URL + sender), when it runs, and who receives it. They are grouped that way
 * here — the legacy is one flat column of ten controls in a modal, where the
 * scheduling field sits between two unrelated selects.
 *
 * `الإدارات` is a real multi-select rather than the legacy's select2 with a
 * sentinel "تحديد الكل" OPTION — an option that is not a value is a trap: it
 * shows up as a chosen item and posts nothing.
 */
export default function CampaignFormDrawer({
  open,
  campaign,
  templates = [],
  domains = [],
  senders = [],
  groups = [],
  programs = [],
  onClose,
  onSave,
  saving = false,
}) {
  const [values, setValues] = useState(EMPTY)
  const [errors, setErrors] = useState({})

  useEffect(() => {
    if (!open) return
    /* Null-safe: the spread would otherwise let a null from the row REPLACE the default,
       and `values.name.trim()` / `values.groupIds.length` throw on the first submit. */
    setValues(
      campaign
        ? { ...EMPTY, ...Object.fromEntries(Object.entries(campaign).filter(([, v]) => v != null)) }
        : EMPTY,
    )
    setErrors({})
  }, [open, campaign])

  const set = (key) => (value) => {
    setValues((previous) => ({ ...previous, [key]: value }))
    setErrors((previous) => ({ ...previous, [key]: undefined }))
  }

  const dateError = useMemo(() => {
    if (!values.startDate || !values.endDate) return null
    return values.endDate < values.startDate ? S.form.required : null
  }, [values.startDate, values.endDate])

  const submit = (event) => {
    event.preventDefault()
    const next = {}
    if (!values.name.trim()) next.name = S.form.required
    if (!values.templateId) next.templateId = S.form.required
    if (!values.url) next.url = S.form.required
    if (!values.senderId) next.senderId = S.form.required
    if (!values.startDate) next.startDate = S.form.required
    if (!values.endDate) next.endDate = S.form.required
    if (values.groupIds.length === 0) next.groupIds = S.form.required
    /* This was rendered under the end date and never enforced — a campaign that ends before
       it starts submitted happily, and the server's own guard then rejects it. */
    if (dateError) next.endDate = dateError
    setErrors(next)
    if (Object.keys(next).length > 0) return
    onSave?.(values)
  }

  return (
    <FormDrawer
      open={open}
      size="lg"
      formId="campaign-form"
      icon={Fish}
      title={campaign ? S.edit : S.addNew}
      subtitle={S.title}
      submitLabel={S.form.save}
      submitting={saving}
      cancelLabel={S.close}
      onClose={onClose}
      onSubmit={submit}
    >
      {/* A campaign is four decisions: what it sends, where it points, when it
          runs and who receives it. The sections are those four. */}
      <FormBody>
        <FormSection>
          <FormCols>
            <FormRow className="form-span" label={S.form.name} error={errors.name}>
              {(id) => (
                <TextField
                  id={id}
                  value={values.name}
                  onChange={set('name')}
                  invalid={!!errors.name}
                  autoComplete="off"
                />
              )}
            </FormRow>

            <FormRow label={S.form.template} error={errors.templateId}>
              {(id) => (
                <Select
                  id={id}
                  value={values.templateId}
                  onChange={set('templateId')}
                  options={templates}
                  placeholder={S.form.templatePlaceholder}
                />
              )}
            </FormRow>

            <FormRow label={S.form.url} error={errors.url}>
              {(id) => (
                <Select
                  id={id}
                  value={values.url}
                  onChange={set('url')}
                  options={domains}
                  placeholder={S.form.urlPlaceholder}
                />
              )}
            </FormRow>

            <FormRow label={S.form.sender} error={errors.senderId}>
              {(id) => (
                <Select
                  id={id}
                  value={values.senderId}
                  onChange={set('senderId')}
                  options={senders}
                  placeholder={S.form.senderPlaceholder}
                />
              )}
            </FormRow>

            <FormRow label={S.form.program}>
              {(id) => (
                <Select
                  id={id}
                  value={values.programId}
                  onChange={set('programId')}
                  options={programs}
                  placeholder={S.form.programPlaceholder}
                />
              )}
            </FormRow>
          </FormCols>
        </FormSection>

        <FormSection>
          <FormCols>
            <FormRow label={S.form.startDate} error={errors.startDate}>
              {(id) => (
                <DatePicker
                  id={id}
                  value={values.startDate}
                  onChange={set('startDate')}
                  max={values.endDate || undefined}
                />
              )}
            </FormRow>

            <FormRow label={S.form.endDate} error={errors.endDate ?? dateError}>
              {(id) => (
                <DatePicker
                  id={id}
                  value={values.endDate}
                  onChange={set('endDate')}
                  min={values.startDate || undefined}
                />
              )}
            </FormRow>

            <FormRow label={S.form.schedule} hint={S.form.schedulingHint}>
              {(id) => (
                <DatePicker
                  id={id}
                  value={values.sendEmailsBy}
                  onChange={set('sendEmailsBy')}
                  min={values.startDate || undefined}
                  max={values.endDate || undefined}
                />
              )}
            </FormRow>
          </FormCols>
        </FormSection>

        <FormSection>
          <FormCols>
            <FormSpan>
              <GroupPicker
                options={groups}
                selected={values.groupIds}
                onChange={set('groupIds')}
                legend={S.form.groups}
                error={errors.groupIds}
                selectAllLabel={S.form.selectAll}
                filterLabel={S.form.filter}
              />
            </FormSpan>
          </FormCols>
        </FormSection>

        <SwitchField
          checked={values.isActive}
          onChange={set('isActive')}
          label={S.form.active}
        />
      </FormBody>
    </FormDrawer>
  )
}
